import React from 'react';
import { useTranslation } from 'react-i18next';
import SdgIcon from './SdgIcon';
import './EventTags.css';

/* The category an event carries — townhall, workshop, family day, after-work.

   One component rather than three, because the same chips appear in three places and they must
   read identically: on an event row (what this is), in the calendar filter (what I want to see)
   and in the editor (what this should be). A tag comes off the API with an id, a name, an
   optional emoji and an optional colour; the colour is the BU's, so it is applied inline rather
   than baked into a class.

   ★ Tag ids arrive lower-cased from the API (SQL Server upper-cases GUIDs and a freshly created
   row does not), but a caller's `selected` list may come from anywhere, so matching is done on a
   lower-cased key here too. */

const key = (v) => String(v || '').toLowerCase();

/* Read-only: the tags this event has. Renders nothing when there are none, so a row without
   tags collapses instead of leaving a gap. */
export const TagPills = ({ tags, className = '' }) => {
  if (!tags || tags.length === 0) return null;
  return (
    <span className={`etags ${className}`}>
      {tags.map((tag) => (
        <span
          key={tag.id}
          className="etag"
          style={tag.color ? { borderColor: tag.color, color: tag.color } : undefined}
        >
          {tag.emoji ? `${tag.emoji} ` : ''}{tag.name}
        </span>
      ))}
    </span>
  );
};

/* A toggle row. `selected` is a list of ids; `onToggle(id)` gets the one that was clicked and the
   caller owns the list — so the same component drives a filter (many chips on) and an editor
   (the event's own tags) without knowing which it is.

   `counts` (id → how many events carry it) turns it into a filter that says how much is behind
   each chip. Only for the FILTER use: on the editor a count would be answering a question the
   person is not asking. A tag nothing carries still draws, at zero — a category that has fallen
   out of use is worth seeing. */
export const TagChips = ({ tags, selected, onToggle, disabled = false, counts = null,
                          label = null, small = false }) => {
  const tally = counts
    ? new Map(Object.entries(counts).map(([k, v]) => [key(k), v]))
    : null;
  const on = new Set((selected || []).map(key));
  if (!tags || tags.length === 0) return null;
  return (
    <>
      {label && <span className="etags__label">{label}</span>}
      <div className={`etags etags--pick${small ? ' etags--sm' : ''}`}>
        {tags.map((tag) => {
          const active = on.has(key(tag.id));
          return (
            <button
              key={tag.id}
              type="button"
              className={`etag etag--btn ${active ? 'is-on' : ''}`}
              style={active && tag.color
                ? { borderColor: tag.color, backgroundColor: tag.color }
                : undefined}
              onClick={() => onToggle(tag.id)}
              disabled={disabled}
              aria-pressed={active}
            >
              {tag.emoji ? `${tag.emoji} ` : ''}{tag.name}
              {tally && <span className="etag__n">{tally.get(key(tag.id)) || 0}</span>}
            </button>
          );
        })}
      </div>
    </>
  );
};

/* ---------------------------------------------------------------- what an event is FOR

   A category says what KIND of event this is. This says what it is MEANT TO ACHIEVE: the
   company's own themes, values and UN goals, which it already writes down under Programmes.

   Deliberately a pick out of that catalogue and never free text — the whole value of tagging an
   event with "Wellbeing" is that the value on the Programmes screen and the event that serves it
   are the SAME row, so renaming one renames the other and the reporting later has something to
   group by. Each chip carries its own colour: the kind's for themes and values, the goal's own
   official UN colour for an SDG.

   ★ The 17 goals have translated labels in the app, keyed on the official NUMBER, so the name
   coming off the API is only the fallback. */

export const purposeLabel = (t, item) => (
  item.kind === 'sdg'
    ? t(`cso_sdg${item.number}`, { defaultValue: `${item.number}. ${item.name}` })
    : item.name
);

/* The same label with its leading number stripped — everything up to and including the first
   dash. For the two places that draw the goal's own tile beside the words ("SDG 3 – Good Health"
   under a badge already showing a 3 is the number said twice). A theme or a value has no prefix,
   so it comes back untouched. */
export const purposeShort = (label) => String(label || '').replace(/^\S+\s*\d+\s*[–—-]\s*/, '');

/* The order the three kinds are drawn in — widest first — and the label above each row. */
const PURPOSE_ROWS = [['theme', 'eventThemes'], ['value', 'eventValues'], ['sdg', 'eventSdgs']];

/* The three kinds on their own, for a caller that keeps a list per kind — a draft, a filter. */
export const PURPOSE_KINDS = PURPOSE_ROWS.map(([kind]) => kind);

/* How a purpose count is keyed when the chips carry one. On the KIND as well as the id, because
   a caller tallying all three kinds into one map has nothing else to keep them apart by. */
export const purposeCountKey = (kind, id) => `${kind}:${key(id)}`;

/* Read-only: what this event is for. Renders nothing when it is for nothing in particular. */
export const PurposePills = ({ items, className = '' }) => {
  const { t } = useTranslation();
  if (!items || items.length === 0) return null;
  return (
    <span className={`etags ${className}`}>
      {items.map((item) => (
        <span
          key={`${item.kind}-${item.id}`}
          className="etag etag--purpose"
          style={item.color ? { borderColor: item.color, color: item.color } : undefined}
        >
          {/* The goal's own official tile, at pill size. It carries the colour and the number,
              so it says "this is an SDG" before the words are read. */}
          <SdgIcon number={item.number} className="etag__sdg" />
          {purposeLabel(t, item)}
        </span>
      ))}
    </span>
  );
};

/* One kind's chips, with the kind named above them. The shared body of the picker (what this
   event is FOR) and of the filter (what I want to SEE), so the two can never drift into looking
   like different things. `tally` — kind:id → how many — is the filter's alone, and `small` is
   what makes a stack of three rows sit under a heading without shouting over the list it
   filters. */
const PurposeRow = ({ kind, label, rows, on, onToggle, disabled = false,
                      tally = null, small = false }) => {
  const { t } = useTranslation();
  const picked = new Set((on || []).map(key));
  return (
    <div>
      <span className={small ? 'etags__label' : 'bravos-form__label'}>{label}</span>
      <div className={`etags etags--pick${small ? ' etags--sm' : ''}`}>
        {rows.map((item) => {
          const active = picked.has(key(item.id));
          /* ★ Its own colour on the EDGE whether it is picked or not, and filled with it once it
             is. A purpose is the same thing in the picker, in the filter and on the cheer it ends
             up on, so it should be recognisable as itself in all three — and only the edge,
             because a pale company colour set as 0.7rem text is a chip nobody can read. */
          const tint = item.color
            ? (active
              ? { borderColor: item.color, backgroundColor: item.color }
              : { borderColor: item.color })
            : undefined;
          return (
            <button
              key={item.id}
              type="button"
              className={`etag etag--btn ${active ? 'is-on' : ''}`}
              style={tint}
              onClick={() => onToggle(kind, item.id)}
              disabled={disabled}
              aria-pressed={active}
            >
              <SdgIcon number={item.number} className="etag__sdg" />
              {purposeLabel(t, item)}
              {tally && (
                <span className="etag__n">{tally[purposeCountKey(kind, item.id)] || 0}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* The picker: three chip rows, one per kind, each drawn only when the company has declared
   something of that kind. `picks` is `{theme: [id], value: [id], sdg: [id]}` and the caller owns
   it — `onToggle(kind, id)` gets the one that was clicked.

   The whole block disappears when the company has declared nothing at all, with one line saying
   where to go and add some: three empty rows would be three questions nobody can answer yet.

   ★ `note` overrides the line above the rows. The default says what an EVENT is for, which is
   where the picker was born; a cheer is about a person and needed its own sentence, and a
   sentence about events on a screen about a colleague is how somebody decides the chips are not
   meant for them. */
export const PurposePicker = ({ catalog, picks, onToggle, disabled = false, note = null }) => {
  const { t } = useTranslation();
  const cat = catalog || {};
  const empty = PURPOSE_KINDS.every((kind) => (cat[kind] || []).length === 0);
  if (empty) return <p className="ce-note">{t('noPurposeYet')}</p>;
  return (
    <>
      <p className="ce-note">{note || t('eventPurposeNote')}</p>
      {PURPOSE_ROWS.map(([kind, label]) => ((cat[kind] || []).length === 0 ? null : (
        <PurposeRow
          key={kind}
          kind={kind}
          label={t(label)}
          rows={cat[kind]}
          on={(picks || {})[kind]}
          onToggle={onToggle}
          disabled={disabled}
        />
      )))}
    </>
  );
};

/* The same three kinds as a FILTER, a size down: what an event is FOR is the second way to sift
   a list the category chips already sift by kind. Same shape as `TagChips` — the caller owns
   `picks` and gets `onToggle(kind, id)` back.

   ★ Only what the events in front of you actually carry. A company that has adopted twelve goals
   would otherwise get twelve chips under a programme whose events serve two, and ten of them
   would be a promise that finds nothing. Drawn from the CATALOGUE rather than from the events, so
   the order, the names and the colours stay the picker's. */
export const PurposeChips = ({ catalog, picks, onToggle, counts = null }) => {
  const { t } = useTranslation();
  const cat = catalog || {};
  const tally = counts || {};
  return (
    <>
      {PURPOSE_ROWS.map(([kind, label]) => {
        const rows = (cat[kind] || []).filter((item) => tally[purposeCountKey(kind, item.id)]);
        if (rows.length === 0) return null;
        return (
          <PurposeRow
            key={kind}
            kind={kind}
            label={t(label)}
            rows={rows}
            on={(picks || {})[kind]}
            onToggle={onToggle}
            tally={tally}
            small
          />
        );
      })}
    </>
  );
};

/* The flat list the API sends on an event, back into the `{theme, value, sdg}` shape the picker
   and the save both speak. */
export const purposePicks = (items) => {
  const out = { theme: [], value: [], sdg: [] };
  (items || []).forEach((item) => {
    if (out[item.kind]) out[item.kind].push(item.id);
  });
  return out;
};
